import Swal from 'sweetalert2';
import {
	ANNOTATION_TYPES,
	DIVISIONS,
	EVENT_TYPES,
} from '../components/utils/lists';
import { alertSweet, toastSweet } from './sweetAlert2';
import numeral from 'numeral';
import { calculateTimeDifference, timestampToNormalDateTime } from './hours';

export const verifyInputNumberOrder = (inputStr) => {
	// Division suffixes
	const divisionSuffixes = ['P', 'M', 'C', 'V', 'MTY', 'S', 'QRO'];
	// Check if the first four characters form a valid number
	if (!/^\d{4}/.test(inputStr)) {
		return false;
	}

	// Extract the suffix part of the input string
	var suffix = inputStr.slice(4);

	// Check if the suffix is in the divisionSuffixes array
	if (!divisionSuffixes.includes(suffix)) {
		return false;
	}

	// If both conditions are met, return true
	return true;
};

// Is admin?
export const isAdmin = (user) => [1, 2].includes(user?.user_type?.id);

// Compare quantities with a 5% margin error
export const compareQuantities = (num1, num2) => {
	const marginError = 0.05; // 5%
	const difference = Math.abs(num1 - num2);
	const allowedMargin = Math.max(num1, num2) * marginError;
	return difference <= allowedMargin;
};

// Generate Blob from Buffer Array for PDF file
export const generateBlob = async (response) => {
	// Read response as ArrayBuffer
	const arrayBuffer = await response.arrayBuffer();
	// Create Blob from ArrayBuffer
	return new Blob([arrayBuffer], {
		type: 'application/pdf',
	});
};

// Which path
export const whichPath = (apiConfig = false) => {
    // Stage host names
    const HOST_NAME_STAGES = [
        // '127.0.0.1', // Localhost
        'datagram-m2.com', // Datagram M2 (stage)
        '74.208.46.25', // IP Datagram M2 (stage)
    ];
    // Base path
    const basePath = HOST_NAME_STAGES.includes(location.hostname) ? '/pilu/api' : '/api';
	// Return base path
	return apiConfig ? basePath : basePath.replace('/api', '');
}

export const whichURLAdmin = () => {
	const currentHost = window.location.host;
	if (currentHost.includes('pedidos')) {
		return `https://${currentHost}/admin/`;
	} else if (currentHost.includes('localhost')) {
		return 'http://localhost:8000/admin/';
	} else if (currentHost.includes('127.0.0.1')) {
		return 'http://127.0.0.1:8000/admin/';
	}
	return `${currentHost}/admin/`;
};

export const whichEnvURL = (onlyCode = false) => {
	const currentHost = window.location.host;
	const productionURLs = ['pedidos.pilupedidos.com.mx', 'pedidos.piluniformes.com.mx', 'historial.piluniformes.com.mx']
	if (productionURLs.includes(currentHost)) {
		return onlyCode ? 'live' : currentHost;
	} else if (currentHost.includes('8000')) {
		return onlyCode ? 'local' : 'http://localhost:8000/#/';
	}
	return onlyCode ? 'stage' : 'https://datagram-m2.com/';
};

export const superadminContact = '522226754831';

export const emailDevFer = 'flopezramirez@hotmail.com';

export const isInDevEnv = () =>
	document.URL.includes(':8000') || document.URL.includes('datagram');

export const defaultContactsNumbers = [
	{ phone: '522226754831', name: 'Ing. Fernando López Ramírez' },
	{ phone: '522225234300', name: 'Ing. Gerardo López Beristain' },
	{ phone: '522222938488', name: 'Lic. Ángel Serna' },
	{ phone: '522222936073', name: 'Lic. Ricardo Sánchez' },
	{ phone: '522211745711', name: 'Ing. Andrea López Ramírez' },
	{ phone: '522211742308', name: 'Lic. Eduardo Sánchez' },
	{ phone: '522225505238', name: 'Lic. Paulina Sánchez' },
	{ phone: '522227363704', name: 'Lic. Rocío Limón' },
];

export const defaultContactsNumbersDev = [
	{ phone: '522226754831', name: 'Ing. Fernando López Ramírez' },
];

// These ids are from the Contact model, not the User model.
// Also, these contacts are default contacts for every new order.
export const defaultContacts = [
	1, // Ing. Fernando López Ramírez
	5, // Ing. Gerardo López Beristain
	4, // Ing. Andrea López Ramírez
	12, // Lic. Ángel Serna
	14, // Lic. Ricardo Sánchez
	55, // Lic. Eduardo Sánchez
	54, // Lic. Paulina Sánchez
	66, // Lic. Rocío Limón
];

export const defaultContactsDev = [
	1, // Ing. Fernando López Ramírez
];

export const getUniques = (arr) =>
	arr.filter((val, i, ref) => ref.indexOf(val) === i);

export const round2 = (num) => parseFloat(numeral(num).format('0.00'));

const SIZES_LETTERS = [
	'UNITALLA',
	'XCH',
	'2XCH',
	'CH',
	'M',
	'L',
	'XL',
	'2XL',
	'3XL',
	'4XL',
	'5XL',
	'ESP',
];

export const sort = (array) => array.sort((a, b) => a.size - b.size);

// Order sizes by number and word
export const sortSizes = (sizes, kw) => {
	const nums = sizes.filter((item) => isFinite(item[kw]));
	const letters = sizes.filter((item) => !isFinite(item[kw]));

	const finalLetters = SIZES_LETTERS.map(
		(size) => letters?.find((item) => item[kw] === size) || null
	).filter((item) => item !== null);

	return [...sort(nums), ...finalLetters];
};

export const highestPermissions = (user) => {
	if (
		user?.user_type?.name !== 'Asesor' ||
		user.email === 'glopezberistain@hotmail.com'
	)
		return true;
	return false;
};

export const fillEmptyString = (text) => {
	if (text?.length === 0 || !text) return 'No asignado';
	return text;
};

export const highPermissions = (user) =>
	user?.user_type?.name !== 'Operador' ||
	user?.area?.name === 'Mesa de Control';

export const getContactPhones = (contacts = []) =>
	contacts.length > 0 ? contacts.map(({ phone }) => phone) : [];

// Convert to base64
export const toBase64 = (file) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result);
		reader.onerror = (error) => reject(error);
	});

// Admin areas
export const adminAreas = [
	'Mesa de Control',
	'Logística',
	'Facturación',
	'Cobranza',
	'Recepción',
];

// Can this user see money amounts?
export const canUserSeeMoney = (user) => {
	return user.user_type.id === 2 || [1, 24, 25].includes(user?.area?.id);
};

// Is it delayed?
export const isDelayed = (diff, allowed) => {
	if (diff !== null) {
		return diff > allowed;
	}
	return false;
};

// Sort array of objects alphabetically by field
export const sortABC = (array, field) => {
	return array.sort((a, b) => {
		if (a[field] < b[field]) {
			return -1;
		}
		if (a[field] > b[field]) {
			return 1;
		}
		return 0;
	});
};

// Money
export const money = (amount) => `${formatterCurrency(amount)}`;

// Get the float value of a money string
export const getFloatValueMoney = (value) => {
	// Remove non-numeric characters using regular expression
	const numericString = value.replace(/[^0-9.]/g, '');

	// Parse the numeric string as a float
	const floatValue = parseFloat(numericString);

	return floatValue;
};

// Get user's fullname
export const getFullName = (user) => `${user.first_name} ${user.last_name}`;

// Get user basic info
export const getUserBasicInfo = (user) => ({
	id: user.id,
	name: getFullName(user),
	email: user.email,
	division: user.division,
	phone: user.phone_number,
	user_type: user.user_type,
	area: user.area,
});

// Copy to clipboard
export const copy2Clipboard = (text) => {
	navigator.clipboard.writeText(text);
};

// LOGO SARO
export const LOGO_SARO_LINK =
	'https://firebasestorage.googleapis.com/v0/b/pilu-e73b8.appspot.com/o/logos_pilu%2Flogo-saro-removebg.png?alt=media&token=e131d06d-a1df-422c-9e0f-f957c112db80';

// LOGO PILU
export const LOGO_PILU_LINK =
	'https://firebasestorage.googleapis.com/v0/b/pilu-e73b8.appspot.com/o/logos_pilu%2Flogo-no-bg.png?alt=media&token=19c683d2-b969-4f03-8e8e-46b5c08b6b35';

// LOGO PILU WHITE
export const LOGO_PILU_WHITE_LINK =
	'https://firebasestorage.googleapis.com/v0/b/pilu-e73b8.appspot.com/o/logos_pilu%2Flogo-pilu-no-bg-light.png?alt=media&token=48b37eb0-d527-4513-be0e-e0c5fc8315f8';

// LOGO P WHITE
export const LOGO_P_WHITE_NO_BG = 'https://firebasestorage.googleapis.com/v0/b/pilu-e73b8.appspot.com/o/logos_pilu%2Flogo-p-no-bg-light.png?alt=media&token=c3277d39-faa8-463d-9a8f-8728ab9cd637';

// LOGO P
export const LOGO_P_NO_BG = 'https://firebasestorage.googleapis.com/v0/b/pilu-e73b8.appspot.com/o/logos_pilu%2Flogo-p-no-bg.png?alt=media&token=2380aec3-edac-415b-be52-42f7623a10af';

export const SARO_BG = 'https://firebasestorage.googleapis.com/v0/b/pilu-e73b8.appspot.com/o/logos_pilu%2Flogo-saro-removebg.png?alt=media&token=e131d06d-a1df-422c-9e0f-f957c112db80';

// PILU color
export const blue_pilu = '#143f6c';

// White color HEX
export const white_pilu = '#ffffff';

// Column style
export const columnStyle = {
	display: 'flex',
	flexDirection: 'column',
};

// Row style
export const rowStyle = {
	display: 'flex',
	flexDirection: 'row',
};

// Centered style
export const justCentered = {
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
};

// Grid style
export const borderStyle = {
	'--Grid-borderWidth': '1px',
	borderTop: 'var(--Grid-borderWidth) solid',
	borderLeft: 'var(--Grid-borderWidth) solid',
	borderColor: 'divider',
	'& > div': {
		borderRight: 'var(--Grid-borderWidth) solid',
		borderBottom: 'var(--Grid-borderWidth) solid',
		borderColor: 'divider',
	},
};

// Order areas by hierarchy
export const orderAreasSimple = (array) => {
	let result = [];
	baseAreas.forEach((baseArea) => {
		const areaTmp =
			array.find((area) => area.name === baseArea.name) || null;

		if (areaTmp) {
			result.push({
				...areaTmp,
				orderArea: baseArea.id,
			});
		}
	});
	// Sort by orderArea
	result = result.sort((a, b) => a.orderArea - b.orderArea);
	// Sort by index order
	result = result.sort((a, b) => a.indexOrder - b.indexOrder);

	// Return sorted array
	return result;
};

// Order areas by hierarchy
export const orderAreas = (array, individualAreas) => {
	let result = [];

	// Array withot admin areas
	const operationAreas = baseAreas.filter(
		(item) => !adminAreas.includes(item.name)
	);

	operationAreas.forEach((baseArea) => {
		const areaTmp =
			array.find((area) => area.name === baseArea.name) || null;
		const individualAreaTmp =
			individualAreas.find((area) => area.name === baseArea.name) || null;

		// Keep only active areas
		if (individualAreaTmp?.active) {
			if (areaTmp) {
				result.push({
					...areaTmp,
					orderArea: baseArea.id,
					id: individualAreaTmp.id,
				});
			} else {
				const admin = ['Mesa de Control', 'Logística'].includes(
					individualAreaTmp.name
				);

				result.push({
					name: individualAreaTmp.name,
					active: admin,
					is_delayed: null,
					authorized_days: individualAreaTmp.days,
					entry_date: null,
					final_date: null,
					updated_at: null,
					orderArea: baseArea.id,
					indexOrder: individualAreaTmp.indexOrder,
					id: individualAreaTmp.id,
				});
			}
		}
	});
	// Sort by orderArea
	result = result.sort((a, b) => a.orderArea - b.orderArea);
	// Sort by index order
	result = result.sort((a, b) => a.indexOrder - b.indexOrder);
	// Return sorted array
	return result;
};

// Create our number formatter.
export const formatterCurrency = (num) => {
	if (typeof num === 'string') {
		num = parseFloat(num.replace('$', ''));
	}

	return new Intl.NumberFormat('es-MX', {
		style: 'currency',
		currency: 'MXN',
	})
		.format(num)
		.replace(' MXN', '');
};

export const removeDuplicates = (arr) => {
	return arr.filter((item, index) => arr.indexOf(item) === index);
};

export const notAllowedField = (userType, field) =>
	userType !== 4 ? field : '-';

// Format first letter to be capitalized and the rest lowercase
// capitalizeFirstLetter
export const capLetters = (text) => {
	try {
		if (text?.length > 0) {
			return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
		}
		return '';
	} catch (error) {
		return '';
	}
};

// Capitalize all the given string
export const capitalizeAll = (text) => {
	return text
		.split('')
		.map((char) => char.toUpperCase())
		.join('');
};

// Wait N seconds and reload
export const waitAndReload = (seconds = 1.5) => {
	setTimeout(() => {
		document.location.reload();
	}, seconds * 1000);
};

// Convert object to array
export const obj2Array = (obj) => {
	let array = [];
	for (const name in obj) {
		array.push({
			name: name,
			value: obj[name],
		});
	}
};

export const whichDivison = (division) => {
	return DIVISIONS.find((item) => item.name === division)?.label || null;
};

export const getDivisonCodeByDivisionName = (division) => {
	return DIVISIONS.find((item) => item.label === division)?.name || null;
};

export const whichAnnotation = (annotation) => {
	return (
		ANNOTATION_TYPES.find((item) => item.name === annotation).label || null
	);
};

export const whichEvent = (event) => {
	return EVENT_TYPES.find((item) => item.name === event) || null;
};

export const whichPriority = (priority) => {
	switch (priority) {
		case 'LOW':
			return 'Baja';

		case 'MED':
			return 'Media';

		case 'HIG':
			return 'Alta';

		default:
			return 'No definido';
	}
};

export const whichTypeOfPacking = (typeOfPacking) => {
	switch (typeOfPacking) {
		case 'EMP1':
			return 'Granel';

		case 'EMP2':
			return 'Personalizado';

		case 'EMP3':
			return 'Granel y Personalizado';

		default:
			return null;
	}
};

export const whichTypeOfClient = (typeOfClientCode) => {
	switch (typeOfClientCode) {
		case 'CLI1':
			return 'Nuevo';

		case 'CLI2':
			return 'Recuperado';

		case 'CLI3':
			return 'Cautivo';

		default:
			return null;
	}
};

export const whichGarment = (typeOfGarment) => {
	switch (typeOfGarment) {
		case 'TYPE_1':
			return 'Confección';

		case 'TYPE_2':
			return 'Comprar';

		case 'TYPE_3':
			return 'N/A';

		default:
			return null;
	}
};

export const whichStatus = (status) => {
	switch (status) {
		case 'ICF':
			return 'En configuración';

		case 'VAL':
			return 'Validado';

		case 'AUT':
			return 'Autorizado';

		case 'REL':
			return 'Liberado';

		case 'DLY':
			return 'Retrasado';

		// FIXME: Check if I'm gonna use this status
		case 'NST':
			return 'No empezado';

		case 'IPR':
			return 'En progreso';

		case 'FIN':
			return 'Terminado';

		default:
			return 'No definido';
	}
};

export const whichStatusColor = (status) => {
	switch (status) {
		case 'ICF':
			return 'default';

		case 'VAL':
			return 'warning';

		case 'AUT':
			return 'primary';

		case 'REL':
			return 'success';

		case 'DLY':
			return 'error';

		case 'NST':
			return 'warning';

		case 'IPR':
			return 'primary';

		case 'FIN':
			return 'success';

		default:
			return 'primary';
	}
};

// Gretting by the moment of the day
export const getDayMoment = (name = null) => {
	if (name) {
		if (name === 'Luis Ricardo') {
			name = 'Ricardo';
		} else {
			name = name.split(' ')[0];
		}
	}
	let hour = new Date().getHours();
	if (hour < 12) return `Buenos días${name ? `, ${name}` : ''} 👋`;
	if (hour >= 12 && hour < 19)
		return `Buenas tardes${name ? `, ${name}` : ''} 👋`;
	if (hour >= 19) return `Buenas noches${name ? `, ${name}` : ''} 🌙`;
};

// Get role id
export const getRoleId = (role) => {
	switch (role) {
		case 'Administrador':
			return 0;
		case 'Jefe de Área':
			return 1;
		case 'Capturista':
			return 2;
		case 'Reportes':
			return 3;
		default:
			return -1;
	}
};

// Handle clean firebase documents as an array of objects
export const cleanDocs = (docs) => {
	let res = [];
	docs.forEach((doc) => {
		res.push({ uid: doc.id, ...doc.data() });
	});
	return res;
};

// Handle info
export const handleInfo = (msg = null, seconds = 5, alertWindow = true) => {
	if (alertWindow) {
		toastSweet(
			'info',
			msg ? msg : 'Algo salió mal. Intenta de nuevo.',
			seconds
		);
	} else {
		alertSweet(
			'info',
			msg ? msg : 'Algo salió mal. Intenta de nuevo.',
			seconds
		);
	}
};

// Handle error
export const handleError = (
	error,
	msg = null,
	seconds = 5,
	alertWindow = true
) => {
	console.log(error);
	if (alertWindow) {
		toastSweet(
			'error',
			msg ? msg : 'Algo salió mal. Intenta de nuevo.',
			seconds
		);
	} else {
		alertSweet(
			'error',
			msg ? msg : 'Algo salió mal. Intenta de nuevo.',
			seconds
		);
	}
};

// Handle success
export const handleSuccess = (
	msg = null,
	alertWindow = true,
	seconds = 5,
	position = 'bottom-right'
) => {
	if (alertWindow) {
		toastSweet(
			'success',
			msg ? msg : 'Algo salió mal. Intenta de nuevo.',
			seconds,
			position
		);
	} else {
		alertSweet(
			'success',
			msg ? msg : 'Algo salió mal. Intenta de nuevo.',
			seconds
		);
	}
};

// Handle warning
export const handleWarning = (msg, alertWindow = true, seconds = 5) => {
	if (alertWindow) {
		toastSweet('warning', msg, seconds);
	} else {
		alertSweet('warning', msg, seconds);
	}
};

// Handle success login
export const handleSuccessLogin = (displayName) => {
	toastSweet(
		'success',
		`¡Bienvenido${displayName ? ` ${displayName}` : ''}!`
	);
};

// Handle success sent email
export const handleSentEmail = () => {
	Swal.fire({
		title: 'Revisa tu bandeja',
		text: 'Te hemos enviado el correo. Verifica tu bandeja de entrada principal o en correos no deseados.',
		icon: 'success',
		confirmButtonColor: blue_pilu,
	});
};

// Handle reset password
export const handleResetPassword = async () => {
	return await Swal.fire({
		title: 'Reestablecer contraseña',
		input: 'email',
		inputLabel: 'Te enviaremos un correo.',
		confirmButtonColor: blue_pilu,
		confirmButtonText: 'Enviar',
		inputPlaceholder: 'Escribe tu correo electrónico',
	});
};

// Handle delete user
export const handleConfirmModal = async (
	title = null,
	text = null,
	icon = null,
	confirmButtonText = null,
	showCancelButton = true
) => {
	return await Swal.fire({
		title: title,
		text: text,
		confirmButtonColor: '#0157a7',
		icon: icon,
		showCancelButton: showCancelButton,
		cancelButtonColor: '#d33',
		confirmButtonText: confirmButtonText,
	});
};

// Handle pseudo delete
export const handlePseudoDelete = () => {
	toastSweet('success', '¡Usuario eliminado exitosamente!');
};

// Base areas order
export const baseAreas = [
	{ id: 1, name: 'Mesa de Control' },
	{ id: 2, name: 'Logística' },
	{ id: 3, name: 'Almacén y Requerimientos' },
	{ id: 4, name: 'Avíos y Comercios' },
	{ id: 4, name: 'Avíos y Comercializado' },
	{ id: 5, name: 'Tela' },
	{ id: 6, name: 'Patronaje' },
	{ id: 7, name: 'Corte' },
	{ id: 8, name: 'Maquila Interna' },
	{ id: 9, name: 'Maquila Externa' },
	{ id: 10, name: 'Bordado' },
	{ id: 11, name: 'Estampado' },
	{ id: 12, name: 'Acabado' },
	{ id: 13, name: 'Empaque, Embarque y Envío' },
	{ id: 14, name: 'Facturación' },
	{ id: 14, name: 'Recepción' },
];

export const GLOSARY_LINK =
	'https://firebasestorage.googleapis.com/v0/b/pilu-e73b8.appspot.com/o/glosario%2Fglosario_pilu.pdf?alt=media&token=f6f9b824-228f-4caf-8b51-3958d0481a96';

const isValid = (data) => {
	return !(
		data === null ||
		data === undefined ||
		data === 'null' ||
		data === '' ||
		data === '-'
	);
};

export const formatWord = (word) => {
	return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

export const formatClientZone = (city, municipality, state) => {
	const formattedCity = isValid(city) ? `${formatWord(city)}, ` : '';
	const formattedMunicipality = isValid(municipality)
		? `${formatWord(municipality)}, `
		: '';
	const formattedState = isValid(state) ? formatWord(state) : '';

	let zone = `${formattedCity}${formattedMunicipality}${formattedState}`;

	if (zone.length > 0) {
		zone = zone.replace(/,\s*$/, ''); // Remove trailing comma and spaces if present
		zone += '.'; // Add period at the end
	} else {
		zone = 'No asignado';
	}

	return zone;
};

export const isEmptyOrNull = (value) =>
	value?.length === 0 || value === undefined || value === null;

export const validateValues = (values) =>
	values.filter((value) => isEmptyOrNull(value));

// Column style for datagrid (blue pilu)
export const columnStyleCell = {
	headerClassName: 'column-header-pilu',
	headerAlign: 'center',
};

// Helper function to remove empty fields from an item
export const removeEmptyFields = (item) => {
	const filteredItem = {};
	Object.entries(item).forEach(([key, value]) => {
		if (
			value !== null &&
			value !== undefined &&
			value !== '' &&
			value !== '-'
		) {
			filteredItem[key] = value;
		}
	});
	return filteredItem;
};

// Format event data object
export const formatEventData = (event) => {
	delete event.user_ref;
	delete event.coordinates;
	delete event.first_check;
	delete event.second_check;

	event = {
		...event,
		created_at: timestampToNormalDateTime(event.created_at),
		datetime: timestampToNormalDateTime(event.datetime),
		visit_time: `${calculateTimeDifference(
			event.first_check_time,
			event.second_check_time
		)} minutos`,
		user_name: event?.user?.display_name,
		user_email: event?.user?.email,
		first_check_time: timestampToNormalDateTime(event?.first_check_time),
		second_check_time: timestampToNormalDateTime(event?.second_check_time),
		has_been_postpone: event?.has_been_postpone ? 'Sí' : 'No',
		cancelled: event?.cancelled ? 'Sí' : 'No',
		deleted: event?.deleted ? 'Sí' : 'No',
		division: whichDivison(event.division),
	};

	return removeEmptyFields(event);
};
